//@ vendors
const React = require('react');
const { contains, each, get, every, filter, startsWith, find } = require('lodash');

// @ components
const { LoadingSection } = require('commonsComponents/loadingSection');
const { OKBoxTitle } = require('commonsComponents/OKBoxTitle');
const listItem = require('./listItem');

// @ constants
const MORTGAGE_PANEL_STATES = require('constants/mortgageRequestPanelStates');
const { EMPLOYMENT_SITUATION, DOCUMENTS_STATUS } = require('constants/mortgageRequestHolderData');
const {
    text,
    EMPLOYMENT_SITUATION_INVALID,
    LABORAL_LIFE_TYPE_LIST,
    IRPF100_TYPE_LIST,
    IRPF300_TYPE_LIST
} = require('./initialValues');

const getDocumentStatus = (summaryDocumentationState, documentsTypeList, personalDocumentId) => {
    let documentData;
    let documentDataStatus;
    for (let i = 0; documentsTypeList[i]; i++) {
        documentData = find(summaryDocumentationState.data, doc =>
            doc.tipoDocumentoId === documentsTypeList[i] && (!personalDocumentId || doc.numeroDocumento === personalDocumentId)
        );
        documentDataStatus = get(documentData, 'estado', DOCUMENTS_STATUS.NONE) || DOCUMENTS_STATUS.NONE;

        if (
            documentDataStatus !== DOCUMENTS_STATUS.PEND_APORTAR &&
            documentDataStatus !== DOCUMENTS_STATUS.NONE
        ) { break; }
    }
    return documentDataStatus;
};

const getStatus = data => {
    if (
        data.dataProprietor.get('hasBeenFetched') &&
        data.dataProprietor.get('hasBeenPersonalDataFetched') &&
        data.dataProprietor.get('hasBeenPersonalEconomicFetched')
    ) {
        const dataProprietors = data.dataProprietor.toJS();
        const dataHolders = dataProprietors.data;
        const dataPersonal = dataProprietors.personalData;
        const dataEconomic = dataProprietors.economicData;
        const summaryDocumentationState = data.summaryDocumentationState.toJS();
        const isValidDataProprietorState = contains(MORTGAGE_PANEL_STATES.APPROVED_STATES_DATA_PROPRIETOR, data.mortgageState.get('estado'));
        let status = {};

        if (isValidDataProprietorState) {
            status.STEP_VALIDATE = true;
        } else {
            const statusPersonal = [];
            const statusWork = [];
            const statusEconomic = [];
            each(dataHolders, (holder, index) => {
                const personalDocumentId = get(dataPersonal, `${index}.documentoDeIdentidad.identificador`, '') || '';
                const personalExpirationDate = get(dataPersonal, `${index}.documentoDeIdentidad.fechaCaducidad`, '') || '';
                const personalCivilStatus = get(dataPersonal, `${index}.estadoCivil`, '') || '';
                const workEmploymentSituation = get(holder, 'datosEmpleo.situacionLaboral', '') || '';
                const workLevelStudies = get(holder, 'datosEmpleo.nivelEstudios', '') || '';
                const workContractType = get(holder, 'datosEmpleo.tipoContratoTrabajo', '') || '';
                const workIncorporationMonth = get(holder, 'datosEmpleo.mesIncorporacionTrabajo', '') || '';
                const workIncorporationYear = get(holder, 'datosEmpleo.anioIncorporacionTrabajo', '') || '';
                const workIdCea = get(holder, 'datosEmpleo.idCea', '') || '';
                const workDateCea = get(holder, 'datosEmpleo.fechaGenCea', '') || '';
                const workCodeCea = filter(get(holder, 'datosEmpleo', {}), (code, key) => startsWith(key, 'codigoCea'));
                const economicIncome = get(dataEconomic, `${index}.ingresosNetos`, 0) || 0;
                const economicPropertyRegime = get(dataEconomic, `${index}.regimenInmueble`, '') || '';
                const economicCvsCode = get(dataEconomic, `${index}.codigoCvs`, '') || '';
                const employmentSituationInvalid = contains(EMPLOYMENT_SITUATION_INVALID, workEmploymentSituation);
                const employmentIsAutonomo = workEmploymentSituation === EMPLOYMENT_SITUATION.AUTONOMO;
                const documentLaboralLifeValue = getDocumentStatus(summaryDocumentationState, LABORAL_LIFE_TYPE_LIST, personalDocumentId);
                const documentIrpf100Value = getDocumentStatus(summaryDocumentationState, IRPF100_TYPE_LIST, personalDocumentId);
                const documentIrpf130Value = getDocumentStatus(summaryDocumentationState, IRPF300_TYPE_LIST, personalDocumentId);
                const documentLaboralLifeIsValid = documentLaboralLifeValue === DOCUMENTS_STATUS.VERIFICADO;
                const documentIrpf100IsValid = documentIrpf100Value === DOCUMENTS_STATUS.VERIFICADO;
                const documentIrpf130IsValid = documentIrpf130Value === DOCUMENTS_STATUS.VERIFICADO;

                statusPersonal.push(!!personalExpirationDate && !!personalCivilStatus);

                statusWork.push(
                    (!!workEmploymentSituation && workEmploymentSituation !== 'UNKNOWN') &&
                    (!!workLevelStudies && workLevelStudies !== 'UNKNOWN') &&
                    (employmentSituationInvalid || (!!workContractType && workContractType !== 'UNKNOWN')) &&
                    (employmentSituationInvalid || !!workIncorporationMonth) &&
                    (employmentSituationInvalid || !!workIncorporationYear) &&
                    (
                        documentLaboralLifeIsValid ||
                        (!!workIdCea && !!workDateCea && (workCodeCea.length ? every(workCodeCea, val => val !== '') : false))
                    )
                );

                statusEconomic.push(
                    economicIncome > 0 &&
                    (!!economicPropertyRegime && economicPropertyRegime !== 'UNKNOWN') &&
                    (documentIrpf100IsValid || !!economicCvsCode) &&
                    (employmentIsAutonomo ? documentIrpf130IsValid : true)
                );
            });

            status.PERSONAL = statusPersonal.length ? every(statusPersonal, Boolean) : false;
            status.WORK = statusWork.length ? every(statusWork, Boolean) : false;
            status.ECONOMIC = statusEconomic.length ? every(statusEconomic, Boolean) : false;
        }
        return status;
    }
};

const isFetching = data => {
    return !data.dataProprietor.get('hasBeenFetched') ||
        !data.dataProprietor.get('hasBeenPersonalDataFetched') ||
        !data.dataProprietor.get('hasBeenPersonalEconomicFetched') ||
        data.dataProprietor.get('isFetching');
}

const sectionDocumentsHolderInfo = (data) => {
    const status = getStatus(data);
    const textValue = text();
    const list = isFetching(data) ?
        <LoadingSection /> :
        (
            <div>
                {
                    listItem(
                        textValue.SECTION.HOLDER_INFORMATION.ITEM_PERSONAL_DATA,
                        status.STEP_VALIDATE || status.PERSONAL
                    )
                }
                {
                    listItem(
                        textValue.SECTION.HOLDER_INFORMATION.ITEM_WORK_DATA,
                        status.STEP_VALIDATE || status.WORK
                    )
                }
                {
                    listItem(
                        textValue.SECTION.HOLDER_INFORMATION.ITEM_ECONOMIC_DATA,
                        status.STEP_VALIDATE || status.ECONOMIC
                    )
                }
            </div>
        );

    return (
        <div className="mortgage-request-view-process__panel-left-section">
            <OKBoxTitle
                value={textValue.SECTION.HOLDER_INFORMATION.TITLE}
                size="medium"
            />
            {list}
        </div>
    )
}

module.exports = sectionDocumentsHolderInfo;
